/**
 * Socket.IO realtime gateway client.
 *
 * The backend (promostork_backend/src/realtime/socket.ts) authenticates the
 * connection with the JWT passed in `auth.token` and joins the socket to the
 * private `user:<id>` room. Server → client events:
 *
 *   notification:new       { id, type, category, priority, title, body, data, link, imageUrl, audience, createdAt }
 *   notification:read      { id, audience, mount }  — a device/tab marked one read (only when the row changed)
 *   notification:read-all  { mount }  — an inbox ('app' | 'dashboard') was marked all read
 *
 * The room is per user, not per mount: a staff user also gets the Dashboard's
 * events here. NotificationContext skips `audience: 'STAFF'` notices and
 * reads, refetches the count for a read in the Dashboard, and only zeroes the
 * badge for `mount: 'app'` (see backend docs/notifications.md §5).
 *
 * Singleton: one connection per tab, rebuilt whenever the auth token changes
 * OR when the cached socket is permanently dead (reconnection attempts
 * exhausted / force-disconnected). Callers that bind listeners must rebind
 * when connectSocket() returns a new instance — NotificationContext does this
 * from its tab-focus handler.
 * socket.io-client handles reconnection/backoff internally.
 *
 * socket.io-client (~40 kB) is loaded on the first connectSocket() call, a
 * chunk of its own: only signed-in visitors open a socket, so guests and
 * crawlers never download it and it is off the first page load's critical
 * path (WX7). Hence connectSocket() resolves asynchronously, to null when the
 * call was overtaken while the client loaded (logout, or another token) or
 * the chunk failed to load (the focus handler tries again).
 */
import type { Socket } from 'socket.io-client';
import { BASE_URL } from '@/constants/config';

let socket: Socket | null = null;
let currentToken: string | null = null;
// socket.io v4 keeps `socket.active` true even after the manager exhausts its
// reconnectionAttempts (`reconnect_failed` fires but the socket's manager
// subscriptions are never destroyed), so terminal failure is tracked here.
let reconnectExhausted = false;
// The token the latest connectSocket() call asked for; null after
// disconnectSocket(). A call that finds it changed once the client has
// loaded was overtaken and opens nothing.
let wantedToken: string | null = null;
let client: Promise<typeof import('socket.io-client')> | null = null;

function loadClient(): Promise<typeof import('socket.io-client')> {
  client ??= import('socket.io-client');
  return client;
}

// Reuse the cached socket only while it can still recover on its own:
// connected, or `active` (socket.io v4: still auto-reconnecting). Once
// reconnection attempts are exhausted, or it was force-disconnected on an auth
// error (which clears `active`), it will never come back by itself — tear it
// down and build a fresh instance.
function reusable(token: string): Socket | null {
  return socket && currentToken === token && !reconnectExhausted && (socket.connected || socket.active)
    ? socket
    : null;
}

function teardown(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  currentToken = null;
  reconnectExhausted = false;
}

export async function connectSocket(token: string): Promise<Socket | null> {
  wantedToken = token;
  const cached = reusable(token);
  if (cached) return cached;
  let io: (typeof import('socket.io-client'))['io'];
  try {
    ({ io } = await loadClient());
  } catch {
    // Offline or a stale deploy: forget the failed import so a later call retries.
    client = null;
    return null;
  }
  if (wantedToken !== token) return null;
  // Another call for this token may have connected while this one waited.
  const connected = reusable(token);
  if (connected) return connected;
  teardown();
  currentToken = token;
  socket = io(BASE_URL, {
    auth: { token },
    // Allow the HTTP long-poll fallback. Websocket-only meant that if the WS
    // upgrade failed (proxy, dev tunnel, etc.) the client could never connect
    // and just retried forever — hammering the backend on every page.
    transports: ['websocket', 'polling'],
    reconnection: true,
    // Bounded so an unreachable / misconfigured realtime server can't spam the
    // backend indefinitely. After this we give up until the next token change
    // or tab focus (the focus handler in NotificationContext re-syncs).
    reconnectionAttempts: 8,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000,
    timeout: 10000,
  });

  // The manager gave up after `reconnectionAttempts` — this socket will never
  // recover on its own. Flag it so the next connectSocket() call (e.g. from
  // the tab-focus handler in NotificationContext) rebuilds a fresh connection.
  socket.io.on('reconnect_failed', () => {
    reconnectExhausted = true;
  });

  // If the server explicitly rejects the connection (bad/expired token), stop
  // reconnecting — retrying with the same bad token would loop forever.
  socket.on('connect_error', (err: Error) => {
    const msg = String(err?.message ?? '').toLowerCase();
    if (
      msg.includes('auth') ||
      msg.includes('unauthorized') ||
      msg.includes('token') ||
      msg.includes('jwt')
    ) {
      if (socket?.io?.opts) socket.io.opts.reconnection = false;
      socket?.disconnect();
    }
  });

  return socket;
}

/** Close the socket (logout); a connectSocket() still loading the client opens nothing. */
export function disconnectSocket(): void {
  wantedToken = null;
  teardown();
}

export function getSocket(): Socket | null {
  return socket;
}

/** True when the singleton socket exists and is currently connected. */
export function isSocketConnected(): boolean {
  return !!socket?.connected;
}

/** Opt-in to a live entity feed (rooms `entity:<TYPE>:<id>`). */
export function joinEntityRoom(entityType: string, entity: string): void {
  socket?.emit('socket:join', { entityType, entity });
}

export function leaveEntityRoom(entityType: string, entity: string): void {
  socket?.emit('socket:leave', { entityType, entity });
}
